import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import type { PlanLimits } from '../types';

/**
 * The company's plan and whether it has used its free test
 * (docs/v2-product-design.md §4). `plan` stays null until loaded or when the
 * request fails; callers then don't gate in the UI and the server's 402 opens
 * the upgrade prompt instead.
 */
export function usePlan(companyId: string) {
  const [plan, setPlan] = useState<PlanLimits | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const next = await api.getBillingLimits(companyId);
      setPlan(next);
      return next;
    } catch (err) {
      console.error('Failed to load plan:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { plan, loading, refresh };
}

/**
 * After the in-app purchase succeeds, Pro is granted by our webhook, so poll
 * the billing record for a short while. Resolves true once Pro is active.
 */
export async function waitForProAccess(companyId: string, attempts = 10): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      if ((await api.getBillingStatus(companyId)).hasProAccess) return true;
    } catch {
      // ignore and retry
    }
  }
  return false;
}
